import { Root, type RootProps } from 'Root/Root';
import {
  AcceptInvite,
  Agency,
  Analytics,
  BacklinkAnalysis,
  BillingHome,
  CallTrackingRoutes,
  CancellationWizard,
  ContentOverview,
  CreateAccount,
  CreateAccountComplete,
  CreateClient,
  DomainOverview,
  Go,
  GoogleSearchConsole,
  IndexRedirect,
  KeywordExplorerOrUpsell,
  MembershipEditor,
  Onboarding2025Routes,
  OnboardingRoutes,
  PurchaseBundle,
  SearchPerformance,
  SignUp,
  Site,
  SiteAuditRoutes,
  SiteSettingsDelete,
  SmoothUpgrade,
  SubscriptionUpdated,
  SuperActionItemTemplatesList,
  SuperActionItemTemplatesUpsert,
  SuperAuthorizedPage,
  SuperBundlesCreate,
  SuperBundlesList,
  SuperFeaturesCreate,
  SuperFeaturesEdit,
  SuperFeaturesList,
  SuperProductsEdit,
  SuperProductsList,
  SuspenseWithFallback,
  SwitchAccount,
  TeamMembersRoutes,
} from 'lazy-routes';
import { AuthorizedPage } from 'pages/AuthorizedPage/AuthorizedPage';
import { CallTrackingLayout } from 'pages/CallTracking/CallTrackingLayout';
import { ErrorPage } from 'pages/ErrorPage/ErrorPage';
import { Home } from 'pages/Home/Home';
import { PageNotFound } from 'pages/PageNotFound/PageNotFound';
import { BaseTrackingScripts } from 'pages/PurchaseBundle/BaseTrackingScripts';
import { Navigate, Outlet, type RouteObject } from 'react-router';
import { PageWithAuthenticatedNavs } from 'turnip/PageWithAuthenticatedNavs/PageWithAuthenticatedNavs';
import { PageWithFooter } from 'turnip/PageWithFooter/PageWithFooter';

/**
 * WARN:
 * When changing this file, force Chromatic to run all stories by running
 * Chromatic with TurboSnap disabled:
 * `docker compose run node npm run chromatic-all`.
 *
 * Background:
 * Changing any page was causing Chromatic to run the snapshots for all pages
 * because all pages share this common file. This slowed down Chromatic runs
 * and increased our costs.
 *
 * To improve that, we pass this file to the Chromatic `--untraced` flag, which
 * ignores it in the dependency chain used to detect which snapshots to run for
 * which stories.
 *
 * See:
 * - https://www.chromatic.com/docs/turbosnap/setup/#avoid-re-testing-dependent-stories-when-certain-files-changed.
 * - https://www.chromatic.com/docs/configure/#onlychanged
 */

/**
 * This function returns the routes for the app
 *
 * By using a function to add our high-level providers and components to the
 * component tree, we are able to inject test values when routes are rendered
 * during tests.
 */
export const createRoutes = ({
  queryClient,
  mixpanel,
  googleAnalyticsId,
  hubSpotId,
}: RootProps): RouteObject[] => [
  {
    element: (
      <SuspenseWithFallback title="Loading app">
        <Root
          queryClient={queryClient}
          mixpanel={mixpanel}
          googleAnalyticsId={googleAnalyticsId}
          hubSpotId={hubSpotId}
        >
          <Outlet />
        </Root>
      </SuspenseWithFallback>
    ),
    errorElement: (
      <SuspenseWithFallback title="Loading app">
        <Root
          queryClient={queryClient}
          mixpanel={mixpanel}
          googleAnalyticsId={googleAnalyticsId}
          hubSpotId={hubSpotId}
        >
          <PageWithFooter>
            <ErrorPage />
          </PageWithFooter>
        </Root>
      </SuspenseWithFallback>
    ),
    children: appRoutes,
  },
];

const errorRoute: RouteObject = {
  path: 'error-route',
  Component: () => {
    throw new Error('test');
  },
};

const appRoutes: RouteObject[] = [
  /**
   * Sign-up routes
   */
  {
    element: (
      <BaseTrackingScripts>
        <Outlet />
      </BaseTrackingScripts>
    ),
    children: [
      {
        element: (
          <PageWithFooter gapBeforeFooter={false}>
            <Outlet />
          </PageWithFooter>
        ),
        children: [
          { path: 'purchase-bundle', element: <PurchaseBundle /> },
          {
            path: 'create-account',
            children: [
              { index: true, element: <PurchaseBundle /> },
              { path: 'complete', element: <CreateAccountComplete /> },
            ],
          },
        ],
      },
      { path: 'sign-up', element: <SignUp /> },
      { path: 'onboarding/create-account', element: <CreateAccount /> },
    ],
  },

  /**
   * Accepting an account invitation
   */
  { path: 'account/team-members/accept/:uuid', element: <AcceptInvite /> },

  /**
   * Authorized routes
   */
  {
    element: (
      <PageWithFooter>
        <AuthorizedPage>
          <Outlet />
        </AuthorizedPage>
      </PageWithFooter>
    ),
    children: [
      { path: 'onboarding/*', element: <OnboardingRoutes /> },
      {
        path: 'account/:accountId/onboarding/*',
        element: <Onboarding2025Routes />,
      },

      {
        element: (
          <PageWithAuthenticatedNavs>
            <Outlet />
          </PageWithAuthenticatedNavs>
        ),
        errorElement: <ErrorPage />,
        children: [
          { index: true, element: <IndexRedirect /> },

          { path: 'go', element: <Go /> },

          { path: 'home', element: <Home /> },

          {
            path: 'account/:accountId',
            children: [
              { index: true, element: <Site /> },
              { path: 'site/:siteId', element: <Site /> },
              { path: 'team-members/*', element: <TeamMembersRoutes /> },
              {
                path: 'agency',
                element: <Agency />,
                children: [
                  { path: 'clients/create', element: <CreateClient /> },
                ],
              },

              // Used for tests:
              {
                path: 'test-route',
                element: <div>Authorized account route</div>,
              },
            ],
          },

          {
            path: 'account',
            children: [
              {
                path: 'billing',
                children: [
                  { index: true, element: <BillingHome /> },
                  { path: 'cancel', element: <CancellationWizard /> },
                  { path: 'edit', element: <MembershipEditor /> },
                  { path: 'upgrade', element: <SmoothUpgrade /> },
                  {
                    path: 'updated',
                    element: <SubscriptionUpdated />,
                  },
                  {
                    path: 'test-route',
                    element: <div>Authorized billing management route</div>,
                  },
                ],
              },
              { path: 'call-tracking/*', element: <CallTrackingRoutes /> },
              { path: 'team-members/*', element: <TeamMembersRoutes /> },
              {
                path: 'switch/:accountId',
                element: <SwitchAccount />,
              },
            ],
          },
          { path: 'keyword-explorer', element: <KeywordExplorerOrUpsell /> },

          /**
           * Single-site routes
           */
          {
            path: 'account/site/:siteId',
            children: [
              // There is currently no root single-site page, so we render the 404
              // page. This path may be used in the future.
              { index: true, element: <PageNotFound contained={false} /> },
              { path: 'analytics', element: <Analytics /> },
              { path: 'content-overview', element: <ContentOverview /> },
              {
                path: 'settings',
                children: [
                  {
                    path: 'google-search-console',
                    element: <GoogleSearchConsole />,
                  },
                  { path: 'delete', element: <SiteSettingsDelete /> },
                  // Used for tests:
                  {
                    path: 'test-route',
                    element: <div>Authorized site settings route</div>,
                  },
                ],
              },
              {
                path: 'seo',
                children: [
                  { index: true, element: <Navigate to="domain-overview" /> },
                  { path: 'domain-overview', element: <DomainOverview /> },
                  { path: 'backlink-analysis', element: <BacklinkAnalysis /> },
                  {
                    path: 'search-performance',
                    element: <SearchPerformance />,
                  },
                  { path: 'site-audit/*', element: <SiteAuditRoutes /> },
                ],
              },
              // Used for tests:
              errorRoute,
              {
                path: 'test-route',
                element: <div>Authorized single-site route</div>,
              },

              // Using this rather than letting react-router throw a 404 when a
              // single-site route that doesn't exist is requested allows the error
              // element on the single-site routes to catch the error rather than
              // the top-level error element. This means the single-site nav will
              // be rendered, which is a better experience for the user.
              {
                path: '*',
                element: null,
                loader: () => {
                  throw new Response('Not found', { status: 404 });
                },
              },
            ],
          },
        ],
      },

      /**
       * Account-based Call Tracking route. Kept separate from the group above so
       * it renders a simplified nav bar (see CallTrackingLayout) instead of the
       * full authenticated nav.
       */
      {
        path: 'account/:accountId/call-tracking/*',
        element: (
          <CallTrackingLayout>
            <CallTrackingRoutes />
          </CallTrackingLayout>
        ),
        errorElement: <ErrorPage />,
      },

      /**
       * Superadmin routes
       */
      {
        path: 'super',
        element: (
          <PageWithAuthenticatedNavs>
            <SuperAuthorizedPage>
              <Outlet />
            </SuperAuthorizedPage>
          </PageWithAuthenticatedNavs>
        ),
        children: [
          {
            path: 'bundles',
            children: [
              { index: true, element: <SuperBundlesList /> },
              { path: 'create', element: <SuperBundlesCreate /> },
            ],
          },
          {
            path: 'features',
            children: [
              { index: true, element: <SuperFeaturesList /> },
              { path: 'create', element: <SuperFeaturesCreate /> },
              { path: 'edit/:id', element: <SuperFeaturesEdit /> },
            ],
          },
          {
            path: 'products',
            children: [
              { index: true, element: <SuperProductsList /> },
              { path: ':stripeId', element: <SuperProductsEdit /> },
            ],
          },
          {
            path: 'action-item-templates',
            children: [
              { index: true, element: <SuperActionItemTemplatesList /> },
              {
                path: 'upsert/:id?',
                element: <SuperActionItemTemplatesUpsert />,
              },
            ],
          },
          {
            path: 'test-route',
            element: <div>Authorized superadmin route</div>,
          },
        ],
      },

      // Used for tests:
      errorRoute,
      {
        path: 'test-route',
        element: <div>Authorized route</div>,
      },
    ],
  },
];
